/**
 * TanStack Query hooks for the token-keyed invitation flow.
 *
 *   useInvitation(token)         — public preview (no session needed)
 *   useAcceptInvitation()        — POST /accept (requires session w/ matching email)
 *   useDeclineInvitation()       — POST /decline (no session needed)
 *   useResendInvitation()        — POST /orgs/:id/invitations/:invId/resend
 *
 * Server-side error envelope codes:
 *   not_found             — bad token
 *   invitation_expired    — past expires_at (HTTP 410)
 *   invitation_already_used — already claimed   (HTTP 410)
 *   forbidden             — email mismatch       (HTTP 403)
 *   conflict              — cross-role conflict  (HTTP 409)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'

export type InvitationRole = 'owner' | 'member' | 'actor' | 'supervisor'

export interface InvitationPreview {
  email: string
  role: InvitationRole
  expires_at: string
  organization: {
    id: string
    name: string
    slug: string
  }
  project_id: string | null
  character: {
    id: string
    display_name: string
    char_code: string
  } | null
}

export interface AcceptInvitationResponse {
  organization_id: string
  organization_slug: string
  role: InvitationRole
  /** Server-suggested next route, role-aware. */
  redirect_to: string
}

const invitationKey = (token: string) => ['invitation', token] as const

/** Public preview of an invitation. Disabled until a token is provided. */
export function useInvitation(token: string | undefined) {
  return useQuery({
    queryKey: invitationKey(token ?? ''),
    queryFn: () => api<InvitationPreview>(`/api/v1/invitations/${token}`),
    enabled: Boolean(token),
    // Don't auto-retry on 404/410 — those are terminal states.
    retry: false,
  })
}

/** Accept an invitation. Caller must be signed in with the invited email. */
export function useAcceptInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) =>
      api<AcceptInvitationResponse>(`/api/v1/invitations/${token}/accept`, {
        method: 'POST',
      }),
    onSuccess: (_data, token) => {
      // Invalidate the preview (status changes) and the user's profile
      // (membership/role may have changed).
      void qc.invalidateQueries({ queryKey: invitationKey(token) })
      void qc.invalidateQueries({ queryKey: ['account', 'profile'] })
      void qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

/** Decline an invitation. Public — no session needed. */
export function useDeclineInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) =>
      api<{ ok: true }>(`/api/v1/invitations/${token}/decline`, { method: 'POST' }),
    onSuccess: (_data, token) => {
      void qc.invalidateQueries({ queryKey: invitationKey(token) })
    },
  })
}

export interface ResendInvitationResponse {
  id: string
  expires_at: string
  invite_url: string
}

/** Owner-only — re-send the magic link email and bump expires_at. */
export function useResendInvitation(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      api<ResendInvitationResponse>(`/api/v1/orgs/${orgId}/invitations/${invitationId}/resend`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['org', orgId, 'invitations'] })
    },
  })
}

export interface PendingInvitation {
  id: string
  email: string
  role: InvitationRole
  project_id: string | null
  character_id: string | null
  created_at: string
  expires_at: string
  claimed_by_user_id: string | null
  claimed_at: string | null
}

/** List pending invitations for a producer's org settings page. */
export function useOrgInvitations(orgId: string, status: 'pending' | 'all' = 'pending') {
  return useQuery({
    queryKey: ['org', orgId, 'invitations', status] as const,
    queryFn: () =>
      api<{ items: PendingInvitation[] }>(`/api/v1/orgs/${orgId}/invitations?status=${status}`),
    enabled: Boolean(orgId),
  })
}
